<!-- This widget visualizes an image field with a colorfield as fallback.

If no image is specified, the color should be used.

-->

<template>
  <v-form
    ref="form"
    class="px-2"
    @submit.prevent
  >
    <!-- Optional warning messages -->
    <v-layout
      v-for="(warning, warningIndex) in warnings"
      :key="'widget-field-warning-' + warningIndex"
      align-center
      class="mb-2"
    >
      <v-icon
        color="warning"
        class="mr-2"
      >
        error_outline
      </v-icon>
      <span class="warning--text">
        {{ warning.prefix }}: <b>{{ warning.fieldLabel }}</b>
      </span>
    </v-layout>
    <v-layout
      justify-center
      align-center
      class="overlay-options-field-bg"
    >
      <!-- Image -->
      <overlay-data-image-field
        v-if="imageField && isImageField(imageField)"
        ref="imagefield"
        :field="imageField"
        :value.sync="imageField.value"
        background-class=""
        @update:value="$emit('changed')"
        @change-image="$emit('change-image', $event)"
      >
        <v-layout
          slot="extraButtons"
          align-center
        >
          <!-- Color-instead-of-image button, when image is set -->
          <v-btn
            v-if="imageField.value && colorField && isColorField(colorField)"
            icon
            class="ml-0 my-0 grey--text text--darken-1"
            :title="$t('imageWidgetAlternativelyChooseBackgroundColor')"
            @click="onUseColorInsteadOfImageButtonClicked()"
          >
            <v-icon>format_color_fill</v-icon>
          </v-btn>
          <!-- Image Size (optional) -->
          <v-slide-y-transition>
            <v-slider
              v-if="
                imageField &&
                  imageField.value &&
                  sizeField &&
                  isRangeField(sizeField)
              "
              :max="sizeField.schema.maximum"
              :min="sizeField.schema.minimum"
              :step="sizeField.schema.multipleOf"
              :title="sizeField.label || ''"
              :value="sizeField.value"
              class="mt-0 pl-2 pr-3"
              hide-details
              prepend-icon="open_in_full"
              @click:prepend="toggleImageSizeValue"
              @input="onSizeChanged"
            />
          </v-slide-y-transition>
        </v-layout>
      </overlay-data-image-field>
      <!-- Color when no image is set -->
      <overlay-data-color-field
        v-if="colorField"
        v-show="
          imageField &&
            !imageField.value &&
            colorField &&
            isColorField(colorField)
        "
        ref="colorfield"
        :color-palette="colorPalette"
        :field="colorField"
        :render-key="colorfieldRenderKey"
        :title="$t('imageWidgetAlternativelyChooseBackgroundColor')"
        :value.sync="colorField.value"
        background-class=""
        no-label
        class="pr-3"
        :class="[imageField && imageField.data.label ? 'mt-5' : 'mt-3']"
        style="max-width: 64px"
        small-inline-button
        @update:value="$emit('changed')"
      />
    </v-layout>
  </v-form>
</template>

<script>
import {
  isColorField,
  isImageField,
  isRangeField,
  OverlayOptionsField,
  OverlayOptionsFieldWarning,
} from '@/mixins/overlayOptions.js';

import OverlayDataColorField from '@/components/OverlayDataColorField';
import OverlayDataImageField from '@/components/OverlayDataImageField';

export default {
  components: {
    OverlayDataColorField,
    OverlayDataImageField,
  },
  props: {
    /**
     * Name of the widget as used in overlay options.
     *
     * Needed to backtrack validation status to its group.
     */
    name: {
      type: String,
      required: true,
    },
    image: {
      type: OverlayOptionsField,
      required: true,
    },
    color: {
      type: OverlayOptionsField,
      default: null,
    },
    size: {
      type: OverlayOptionsField,
      default: null,
    },
    colorPalette: {
      type: Array,
      default: null,
    },
    /**
     * Optional warning messages shown above the interface.
     */
    warnings: {
      type: Array[OverlayOptionsFieldWarning],
      default: null,
    },
  },
  data() {
    // We have to mirror the props as data attributes so they are reactive.
    // See: https://vuejs.org/v2/guide/components-props.html#One-Way-Data-Flow
    return {
      imageField: this.image,
      colorField: this.color,
      sizeField: this.size,

      colorfieldRenderKey: 0,
    };
  },
  computed: {
    fields() {
      return [this.imageField, this.colorField, this.sizeField];
    },
    providedFields() {
      return this.fields.filter((field) => !!field);
    },
  },
  created() {
    this.registerSetValueFromOutsideEventListener();
  },
  methods: {
    isColorField: isColorField,
    isImageField: isImageField,
    isRangeField: isRangeField,
    toggleImageSizeValue() {
      if (this.sizeField.value >= this.sizeField.schema.maximum) {
        this.sizeField.value = this.sizeField.schema.minimum;
      } else if (this.sizeField.value == this.sizeField.schema.minimum) {
        const middle =
          this.sizeField.schema.minimum +
          (this.sizeField.schema.maximum - this.sizeField.schema.minimum) / 2;
        this.sizeField.value = middle;
      } else {
        this.sizeField.value = this.sizeField.schema.maximum;
      }
      this.$emit('changed');
    },
    onSizeChanged(size) {
      this.sizeField.value = size;
      this.$emit('changed');
    },
    onUseColorInsteadOfImageButtonClicked() {
      if (!this.$refs.imagefield) {
        return;
      }
      this.$refs.imagefield.askClearImage();
      this.$nextTick(() => {
        const cleared = !this.imageField.value;
        if (!cleared || !this.$refs.colorfield) {
          return;
        }
        this.$refs.colorfield.colorfieldShowPickerDialog = true;
      });
    },
    validate() {
      return this.$refs.form.validate();
    },
    registerSetValueFromOutsideEventListener() {
      this.$bus.$on('set-overlay-data-field-value', (fieldName, newValue) => {
        for (const field of this.providedFields) {
          if (field.name !== fieldName) {
            continue;
          }
          if (this.isColorField(field)) {
            // Force ColorPicker to rerender so the displayed color matches.
            this.$nextTick(() => {
              this.colorfieldRenderKey += 1;
            });
          }
          field.value = newValue;

          // Ensure e.g. image replacements trigger a preview update.
          this.$emit('changed');
        }
      });
    },
    getOverlayData() {
      const widgetOverlayData = {
        _labels: {},
      };
      for (const field of this.providedFields) {
        let value = field.value;

        widgetOverlayData[field.name] = value;
        widgetOverlayData._labels[field.name] = field.label;
      }
      return widgetOverlayData;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/OverlayDataWidgetImage.vue